import { Icon, StackItem, UtilityText } from "@leagueplatform/genesis-core";
import type { GDSColor } from "@leagueplatform/genesis-core";
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from "@leagueplatform/masonry-engine";

const DEFAULT_BULLET_ICON = "statusLightStatus";
const DEFAULT_BULLET_ICON_TINT: GDSColor = "$onSurfaceIconPrimary";

const BULLET_ICON_ALIASES: Record<string, string> = {
  tinyStatusLight: "statusLightStatus",
};

type MasonryBulletedListItemProperties = {
  text: string;
  bulletIcon?: string;
  bulletIconColor?: string;
};

type MasonryBulletedListItemNode = MasonryEngineNode<
  "bulletedListItem",
  MasonryBulletedListItemProperties
>;

type MasonryBulletedListItemRendererProps =
  MasonryEngineNodeRendererProps<MasonryBulletedListItemNode>;

const isHexColor = (value: string) => value.startsWith("#");

const resolveTint = (
  bulletIconColor: string | undefined
): GDSColor | undefined => {
  if (bulletIconColor === undefined) return DEFAULT_BULLET_ICON_TINT;
  if (isHexColor(bulletIconColor)) return undefined;
  return `$${bulletIconColor}` as GDSColor;
};

export const MasonryBulletedListItemRenderer = ({
  text,
  bulletIcon = DEFAULT_BULLET_ICON,
  bulletIconColor,
}: MasonryBulletedListItemRendererProps) => {
  const resolvedBulletIcon = BULLET_ICON_ALIASES[bulletIcon] ?? bulletIcon;
  const usingHexColor =
    bulletIconColor !== undefined && isHexColor(bulletIconColor);
  const tint = resolveTint(bulletIconColor);

  return (
    <StackItem
      as="li"
      css={{
        gap: "$half",
        flexDirection: "row",
        maxWidth: "700px",
      }}
    >
      <Icon
        icon={resolvedBulletIcon}
        size="$one"
        tint={tint}
        css={{
          marginBlockStart: "$eighth",
          flex: "0 0 auto",
          ...(usingHexColor && { color: bulletIconColor }),
        }}
      />
      <UtilityText
        css={{
          flex: "1 1 0%",
          typography: "$bodyTwo",
          color: "$onSurfaceTextPrimary",
        }}
      >
        {text}
      </UtilityText>
    </StackItem>
  );
};
